import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { useInfiniteQuery } from "@tanstack/react-query";
import type { LiveAssistSession } from "@backstory/protocol";
import { EndUser, endUserInitials } from "../../components/EndUser";
import { EmptyLiveMark, ErrorState, Skeleton } from "../../components/States";
import { AutoLoadMore, DataTable, Dialog, PageHeader, type Column } from "../../components/ui";
import { formatDateTime, formatDuration } from "../../lib/format";
import { liveApi, liveKeys } from "./api";
import { ConsentHistory } from "./components";

const REPLAY_STATE = { sessionReturn: { to: "/live/history", label: "Assist history" } } as never;

/** An assist shorter than this never got going: the console opened and closed. */
const NO_CONNECT_MS = 1000;

function lengthOf(a: LiveAssistSession): number | null {
  return a.endedAt ? new Date(a.endedAt).getTime() - new Date(a.startedAt).getTime() : null;
}

function DurationCell({ a }: { a: LiveAssistSession }) {
  const ms = lengthOf(a);
  if (ms === null) return <span className="badge danger live">live</span>;
  if (ms < NO_CONNECT_MS) return <span className="muted">Didn't connect</span>;
  return <>{formatDuration(ms)}</>;
}

function Tags({ a }: { a: LiveAssistSession }) {
  if (!a.controlActions && !a.recorded) return null;
  return (
    <span className="session-card-badges">
      {a.controlActions > 0 && <span className="badge info">{a.controlActions} remote {a.controlActions === 1 ? "action" : "actions"}</span>}
      {a.recorded && <span className="badge ok">Recorded</span>}
    </span>
  );
}

export function LiveHistoryPage() {
  const [open, setOpen] = useState<LiveAssistSession | null>(null);
  const q = useInfiniteQuery({
    queryKey: liveKeys.history(),
    queryFn: ({ pageParam }) => liveApi.history(pageParam as string | undefined),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor,
  });
  const rows = q.data?.pages.flatMap((page) => page.items) ?? [];

  const consentButton = (a: LiveAssistSession, className: string, testId?: string) =>
    a.consentHistory.length > 0 ? (
      <button type="button" className={className} onClick={() => setOpen(a)} data-testid={testId}>
        Consent history
      </button>
    ) : null;

  const columns: Column<LiveAssistSession>[] = [
    { key: "user", header: "User", className: "live-user-cell", render: (a) => <EndUser id={a.endUserId} name={a.endUserName} avatar /> },
    {
      key: "started",
      header: "Started",
      render: (a) => (
        <span className="cell-stack">
          <strong>{formatDateTime(a.startedAt)}</strong>
          <small>{a.agent.name}</small>
        </span>
      ),
    },
    { key: "duration", header: "Duration", render: (a) => <DurationCell a={a} /> },
    { key: "tags", header: "", render: (a) => <Tags a={a} /> },
    {
      key: "links",
      header: "",
      align: "right",
      render: (a) => (
        <span className="live-history-links">
          {consentButton(a, "btn ghost small", `consent-${a.id}`)}
          <Link to="/sessions/$id" params={{ id: a.sessionId }} state={REPLAY_STATE} className="btn ghost small">
            Replay
          </Link>
        </span>
      ),
    },
  ];

  return (
    <>
      <PageHeader title="Assist history" back={<Link to="/live">← Live</Link>} />
      {q.isLoading && <Skeleton />}
      {q.isError && <ErrorState error={q.error} retry={() => void q.refetch()} />}
      {q.data && rows.length === 0 && <div className="empty"><EmptyLiveMark /><h3>No assist sessions yet.</h3></div>}
      {q.data && rows.length > 0 && (
        <>
          <div className="card live-list-card">
            <DataTable columns={columns} rows={rows} rowKey={(a) => a.id} ariaLabel="Assist history" />
            <div className="session-card-list live-card-list" aria-label="Assist history">
              {rows.map((a) => (
                <div className="session-card live-history-card" key={a.id} data-testid={`history-card-${a.id}`}>
                  <Link to="/sessions/$id" params={{ id: a.sessionId }} state={REPLAY_STATE} className="live-history-open" aria-label={`Replay session with ${a.endUserName ?? a.endUserId ?? "anonymous user"}`}>
                    <span className="session-card-head">
                      <span className="session-card-who">
                        <span className={`session-avatar${a.endUserId ? "" : " anon"}`} aria-hidden="true">
                          {a.endUserId ? endUserInitials(a.endUserId, a.endUserName) : "?"}
                        </span>
                        <span className="session-card-ident">
                          <strong className={a.endUserName ? undefined : "mono"}>{a.endUserName ?? a.endUserId ?? "Anonymous"}</strong>
                          <span className="session-card-when">
                            <span>{formatDateTime(a.startedAt)}</span>
                            <span aria-hidden="true">·</span>
                            <DurationCell a={a} />
                          </span>
                        </span>
                      </span>
                      <svg className="session-card-arrow" width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <path d="m9 6 6 6-6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </span>
                    <Tags a={a} />
                  </Link>
                  <span className="live-history-foot">
                    <span className="muted small">Helped by {a.agent.name}</span>
                    {consentButton(a, "live-history-consent")}
                  </span>
                </div>
              ))}
            </div>
          </div>
          <AutoLoadMore hasMore={!!q.hasNextPage} loading={q.isFetchingNextPage} loadMore={() => void q.fetchNextPage()} label="Loading more assist history" />
        </>
      )}
      <Dialog open={!!open} onClose={() => setOpen(null)} title="Consent history">
        {open && <ConsentHistory history={open.consentHistory} />}
      </Dialog>
    </>
  );
}
